import { Formiz, useForm } from "@formiz/core";
import { isMaxLength, isMinLength, isRequired } from "@formiz/validations";
import {
  CHARACTER_MODERATION_STATUS as GROUPCHAT_MODERATION_STATUS,
  CHARACTER_VISIBILITY,
  CHARACTER_VISIBILITY as GROUP_CHAT_VISIBILITY,
  CharacterType,
  isCharacterTokenCountValid,
  NotificationType,
  useTranslation,
  useUserInfo,
} from "@next-platform/client-common-core";
import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { MAX_TOKEN_LIMIT } from "@next-platform/client-common-core/utils/isCharacterTokenCountValid";
import {
  Button,
  ButtonColorVariant,
  ButtonSize,
  ButtonVariant,
  Checkbox,
  Icon,
  IconSize,
  IconVariants,
  Input,
  Modal,
  RadioGroup,
  Size,
  Text,
  TextArea,
  TextVariants,
  TokenCounter,
} from "@next-platform/client-common-spicy-ui";
import type { Lorebook } from "@next-platform/packages-ts-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { BlockerFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";

import DiscardChangesModalV2 from "@/components/common/DiscardChangesModalV2";
import LorebookSection from "@/components/common/LorebookSection";
import ReactRouterPrompt from "@/components/common/ReactRouterPrompt";
import { FormField } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import { type GroupMembersChatBot, MembersGroupChat, MembersGroupChatDisplayMode } from "@/components/MembersGroupChat";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION, FEATURE_FLAGS, MAXIMUM_TAG_LIMIT, MINIMUM_TAG_LIMIT, ROUTES, THIRD_PARTY_APP_IDS } from "@/constants";
import { CUSTOM_ERROR_MESSAGES } from "@/constants/ErrorMessages";
import { groupChatRequiredFields, MAXIMUM_PARTICIPANT_LIMIT, MINIMUM_PARTICIPANT_LIMIT } from "@/constants/groupChat";
import { useFeatureFlag, useGroupChatDrafts } from "@/hooks";
import { useFieldsSuggestion } from "@/hooks/useFieldsSuggestion";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import useToast from "@/hooks/useToast";
import useTokenCounter from "@/hooks/useTokenCounter";
import AvatarCreate from "@/pages/CreateCharacter/v2/components/AvatarCreate";
import CharacterTags from "@/pages/CreateCharacter/v2/components/CharacterTags";
import GenerateSuggestedFieldButton from "@/pages/CreateCharacter/v2/components/GenerateSuggestedFieldButton";
import ModalLorebook from "@/pages/CreateCharacter/v2/components/ModalLorebook";
import SubmitForReviewModal from "@/pages/CreateCharacter/v2/components/SubmitForReviewModal";
import VisibilityButtons from "@/pages/CreateCharacter/v2/components/VisibilityButtons";
import { CustomErrorMessage, DeepTag } from "@/pages/CreateCharacter/v2/types";
import { useGroupChatForm } from "@/pages/Groupchat/GroupChatFormContext";
import { GroupChatFormFields, GroupChatFormProps } from "@/pages/Groupchat/types";
import hasGroupFormContentChanged from "@/pages/Groupchat/utils/hasGroupFormContentChanged";
import { FormTypes, IDraftGroupCharacter } from "@/types";
import {
  cn,
  findFirstInvalidFormizField,
  isStringContainingURL,
  replaceMessageTokens,
  scrollToFirstInvalidField,
  stripLanguagePrefix,
} from "@/utils";
import { hasVisibilityConflict } from "@/utils/groupChatVisibility";

const FormFieldValueSink = () => null;

const TOKEN_LIMIT = 1600;
const MIN_FIELD_LENGTH = 3;
const MAX_NAME_LENGTH = 20;
const MAX_TITLE_LENGTH = 100;
const MAX_GREETING_LENGTH = 2000;
const MAX_SCENARIO_LENGTH = 4000;
const SUCCESS_MESSAGE_TIMEOUT_MS = 3000;

const EMPTY_CONTEXT_FORM_DATA: Partial<IDraftGroupCharacter> = {};
const EMPTY_CONTEXT_MEMBERS: GroupMembersChatBot[] = [];
const EMPTY_CONTEXT_REFERENCE_VALUES: Record<string, unknown> = {};

function extractDeepTag(input: DeepTag): string {
  while (typeof input !== "string" && input?.value !== undefined) {
    input = input.value;
  }
  return typeof input === "string" ? input : "";
}

function normalizeTagStrings(tags: DeepTag[]): string[] {
  return tags.map(extractDeepTag);
}

function normalizeGreetingCharacterId(value: string | null | undefined) {
  return value ? value : null;
}

function CreateGroupForm({
  submitForManualReview,
  id = "",
  name = "",
  avatarUrl = "",
  greeting = "",
  greetingCharacterId,
  visibility = CHARACTER_VISIBILITY.PRIVATE,
  title = "",
  scenario = "",
  onSave,
  setIsSubmitForManualReview,
  isSubmitting,
  submitError,
  guidelines = false,
  tags = [],
  isEdit,
  draftId,
  isAlreadyDraft = false,
  errorCode,
  participants = [],
  maxParticipants = MAXIMUM_PARTICIPANT_LIMIT,
  moderationStatus,
  lorebooks = [],
  isLorebookAutoDetached = false,
}: GroupChatFormProps) {
  // Convert initial participants to members format if they exist
  const initialMembers = useMemo(
    () =>
      participants?.map(
        (participant: GroupMembersChatBot) =>
          ({
            id: participant.id,
            name: participant.name,
            avatar: participant.avatar,
            avatarIsNsfw: participant.avatarIsNsfw,
            visibility: participant.visibility,
            hasError: false,
          }) as GroupMembersChatBot,
      ) || [],
    [participants],
  );

  const { t } = useTranslation();

  const {
    members: rawContextMembers,
    setMembers: setContextMembers,
    formData: rawContextFormData,
    updateFormData: updateContextFormData,
    draftId: contextDraftId,
    setDraftId: setContextDraftId,
    hasData: hasContextData,
    referenceValues: rawContextReferenceValues,
    setReferenceValues: setContextReferenceValues,
    resetForm,
  } = useGroupChatForm();

  // Context data stamped with another draft/edit session id must not seed this
  const isContextFromOtherForm = Boolean(contextDraftId) && contextDraftId !== (draftId || id);
  const contextFormData = isContextFromOtherForm ? EMPTY_CONTEXT_FORM_DATA : rawContextFormData;
  const contextMembers = isContextFromOtherForm ? EMPTY_CONTEXT_MEMBERS : rawContextMembers;
  const contextReferenceValues = isContextFromOtherForm ? EMPTY_CONTEXT_REFERENCE_VALUES : rawContextReferenceValues;

  const initialValues = useMemo(() => {
    const initialGreetingCharacterId =
      contextFormData.greeting_character_id !== undefined ? contextFormData.greeting_character_id : (greetingCharacterId ?? null);

    return {
      [GroupChatFormFields.COVER]: contextFormData.avatar_url || avatarUrl,
      [GroupChatFormFields.FIRST]: initialGreetingCharacterId,
      [GroupChatFormFields.TYPE]: CharacterType.META,
      [GroupChatFormFields.GREETING]: contextFormData.greeting || greeting,
      [GroupChatFormFields.GUIDELINES]: contextFormData.guidelines ?? guidelines,
      [GroupChatFormFields.NAME]: contextFormData.name || name,
      [GroupChatFormFields.MEMBERS]: contextMembers.length > 0 ? contextMembers : initialMembers,
      [GroupChatFormFields.SCENARIO]: contextFormData.scenario || scenario || "",
      [GroupChatFormFields.TAGS]: contextFormData.tags || tags,
      [GroupChatFormFields.TITLE]: contextFormData.title || title,
      [GroupChatFormFields.VISIBILITY]: contextFormData.visibility || visibility,
      [GroupChatFormFields.SUB_CHARACTERS_ID]: [],
    };
  }, [
    contextFormData,
    contextMembers,
    avatarUrl,
    greetingCharacterId,
    greeting,
    guidelines,
    name,
    initialMembers,
    scenario,
    tags,
    title,
    visibility,
  ]);
  const { pathname, pagePath } = useLocalizedLocation();
  function setDialogMessage() {
    if (submitForManualReview) return t("createGroup:modal.submitForReview.manualReviewMessage");
    const pathSegment = pagePath.split("/")[2];
    let confirmationMessage = t("createGroup:modal.submitForReview.confirmationMessage.default");
    if (pathSegment === "edit") {
      confirmationMessage = t("createGroup:modal.submitForReview.confirmationMessage.edit");
    } else if (pathSegment === "create") {
      confirmationMessage = t("createGroup:modal.submitForReview.confirmationMessage.create");
    }

    return confirmationMessage;
  }

  const confirmText = setDialogMessage();
  const navigate = useNavigate();
  const groupChatForm = useForm();
  const { isValid, setFieldsValues, isStepPristine, values } = groupChatForm;

  // Ref to store initial values snapshot for change detection
  const initialValuesSnapshotRef = useRef<Record<string, unknown> | null>(null);

  const { hasFeatureFlag: hasGroupLorebookFeatureFlag } = useFeatureFlag(FEATURE_FLAGS.RELEASE_GROUP_LOREBOOK_ATTACHMENT_WEB);

  const [stateIsConfirmationModalVisible, setStateIsConfirmationModalVisible] = useState(false);
  const [stateIsLorebookModalOpen, setStateIsLorebookModalOpen] = useState(false);
  const [stateIsDetachLorebookModalOpen, setStateIsDetachLorebookModalOpen] = useState(false);
  const [stateCurrentLorebooks, setStateCurrentLorebooks] = useState(() => contextFormData.lorebooks ?? lorebooks);
  const [stateInitialLorebooks, setStateInitialLorebooks] = useState(lorebooks);
  const [stateIsRequiredFieldsErrorShown, setStateIsRequiredFieldsErrorShown] = useState(false);
  const [stateIsTokenError, setStateIsTokenError] = useState(false);
  const [tokenErrorMessage, setTokenErrorMessage] = useState("");
  // Hoisted above `submitForm` so its guard can reference `isTokenCounterReady`.
  const { countTokens, isReady: isTokenCounterReady } = useTokenCounter();
  const [tokenWarningMessage, setTokenWarningMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [error] = useState("");
  const [stateIsPromptEnabled, setStateIsPromptEnabled] = useState(true);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [groupChatVisibility, setGroupChatVisibility] = useState(visibility || GROUP_CHAT_VISIBILITY.PRIVATE);
  const { userId, username } = useUserInfo();
  const applicationId = basicAppConfig.applicationId;
  const { saveDraft, deleteDraft } = useGroupChatDrafts();
  const groupTags = values[GroupChatFormFields.TAGS];
  const membersValue = (values[GroupChatFormFields.MEMBERS] || []) as GroupMembersChatBot[];
  const { handleGenerateSuggestField, activeGeneratingField } = useFieldsSuggestion({ form: groupChatForm });
  const isGreetingUrlError = isStringContainingURL(values[GroupChatFormFields.GREETING]);
  const isTitleUrlError = isStringContainingURL(values[GroupChatFormFields.TITLE]);
  const isNameUrlError = isStringContainingURL(values[GroupChatFormFields.NAME]);

  const isTitleAndNameFilled =
    values[GroupChatFormFields.NAME]?.trim()?.length >= MIN_FIELD_LENGTH && values[GroupChatFormFields.TITLE]?.trim()?.length >= MIN_FIELD_LENGTH;
  const greetingUrlErrorMessage = `${t("createGroup:validation.greeting.url")} ( ${isGreetingUrlError?.join(", ")} )`;
  const titleUrlErrorMessage = `${t("createGroup:validation.title.url")} ( ${isTitleUrlError?.join(", ")} )`;
  const nameUrlErrorMessage = `${t("createGroup:validation.name.url")} ( ${isNameUrlError?.join(", ")} )`;
  const isGroupVisibilityPublic = groupChatVisibility === GROUP_CHAT_VISIBILITY.PUBLIC;
  const isSelectedTagsValid = (isGroupVisibilityPublic && groupTags?.length >= MINIMUM_TAG_LIMIT) || !isGroupVisibilityPublic;
  const isMemberCountValid = membersValue?.length >= MINIMUM_PARTICIPANT_LIMIT;
  const isWaitingForApproval =
    moderationStatus === GROUPCHAT_MODERATION_STATUS.PENDING ||
    moderationStatus === GROUPCHAT_MODERATION_STATUS.REREVIEW_REQUIRED ||
    moderationStatus === GROUPCHAT_MODERATION_STATUS.MANUAL_REVIEW_REQUIRED;
  const isFirstMessageSelectionEnabled = isMemberCountValid && !isWaitingForApproval;
  const isFixRequired = moderationStatus === GROUPCHAT_MODERATION_STATUS.FIX_REQUIRED;
  const isPermanentlyPrivate = visibility === GROUP_CHAT_VISIBILITY.PERMANENTLY_PRIVATE;
  const prevValuesRef = useRef({});
  const previousMembersCount = useRef(contextMembers.length);
  const hasInitialized = useRef(false);
  const hasChangedLorebooksRef = useRef(false);

  const activeLorebooks = useMemo(() => {
    return hasGroupLorebookFeatureFlag ? stateCurrentLorebooks : [];
  }, [hasGroupLorebookFeatureFlag, stateCurrentLorebooks]);

  const conflictingMembers = useMemo(() => {
    if (!membersValue || (groupChatVisibility !== GROUP_CHAT_VISIBILITY.PUBLIC && groupChatVisibility !== GROUP_CHAT_VISIBILITY.HIDDEN)) {
      return [];
    }
    return membersValue.filter((member: GroupMembersChatBot) => hasVisibilityConflict(groupChatVisibility, member.visibility));
  }, [membersValue, groupChatVisibility]);

  const hasMembersWithErrors = conflictingMembers.length > 0;

  useEffect(() => {
    if (hasChangedLorebooksRef.current) return;

    setStateCurrentLorebooks(contextFormData.lorebooks ?? lorebooks);
    setStateInitialLorebooks(lorebooks);
  }, [contextFormData.lorebooks, lorebooks]);

  // Capture initial snapshot
  useEffect(() => {
    if (!initialValuesSnapshotRef.current && values && Object.keys(values).length > 0) {
      // Initial
      if (!hasContextData || Object.keys(contextReferenceValues).length === 0) {
        initialValuesSnapshotRef.current = { ...values };
        setContextReferenceValues(values);
      } else {
        // Returning from members
        initialValuesSnapshotRef.current = { ...contextReferenceValues };
      }
    }
  }, [values, hasContextData, contextReferenceValues, setContextReferenceValues]);

  useEffect(() => {
    if (!hasInitialized.current) {
      setFieldsValues(initialValues);
      hasInitialized.current = true;
    }
  }, []);

  useEffect(() => {
    const isReturningFromMembers = contextMembers.length > 0 && previousMembersCount.current >= 0;
    const isOnMembersPage = pagePath.includes("/members");

    if (isReturningFromMembers && !isOnMembersPage && hasContextData) {
      if (contextFormData.visibility) {
        setGroupChatVisibility(contextFormData.visibility as CHARACTER_VISIBILITY);
      }
      setFieldsValues({ [GroupChatFormFields.MEMBERS]: contextMembers }, { keepPristine: false });
    }
  }, [contextMembers.length, pagePath, hasContextData, contextMembers, setFieldsValues]);

  // Reset context when entering create mode from edit/draft
  useEffect(() => {
    if (!id && !draftId && !isEdit && isContextFromOtherForm) {
      resetForm();
    }

    previousMembersCount.current = contextMembers.length;
  }, [id, draftId, isEdit, isContextFromOtherForm, contextMembers.length, resetForm]);

  const { displayToast } = useToast();

  const hasLorebookChanged = useMemo(() => {
    if (!hasGroupLorebookFeatureFlag) return false;

    const initialLorebookIds = stateInitialLorebooks.map((lorebook) => lorebook.id).sort();
    const currentLorebookIds = activeLorebooks.map((lorebook) => lorebook.id).sort();

    return JSON.stringify(initialLorebookIds) !== JSON.stringify(currentLorebookIds);
  }, [activeLorebooks, hasGroupLorebookFeatureFlag, stateInitialLorebooks]);

  const hasGroupContentChanged = useMemo(() => {
    return hasGroupFormContentChanged({
      currentValues: values,
      referenceValues: initialValuesSnapshotRef.current,
    });
  }, [values, contextReferenceValues]);

  const hasFormChanged = hasGroupContentChanged || hasLorebookChanged;

  const isPromptVisible = (!isStepPristine || hasLorebookChanged) && hasFormChanged && stateIsPromptEnabled;

  const prepareGroupPayload = useCallback((): IDraftGroupCharacter => {
    const payload = Object.fromEntries(Object.entries(values).filter(([, value]: [string, unknown]) => value !== undefined)) as IDraftGroupCharacter;

    payload[GroupChatFormFields.TYPE] = CharacterType.META;
    payload[GroupChatFormFields.TAGS] = normalizeTagStrings(payload[GroupChatFormFields.TAGS] ?? []);
    payload[GroupChatFormFields.FIRST] = normalizeGreetingCharacterId(values[GroupChatFormFields.FIRST] as string | null | undefined);
    payload.lorebooks = activeLorebooks;
    payload.has_lorebooks = activeLorebooks.length > 0;

    const members = values[GroupChatFormFields.MEMBERS] as GroupMembersChatBot[];
    if (members && members.length > 0) {
      payload[GroupChatFormFields.SUB_CHARACTERS_ID] = members.map((member: GroupMembersChatBot) => member.id);
    }

    delete payload[GroupChatFormFields.MEMBERS];

    return payload;
  }, [activeLorebooks, values]);

  const handleSave = useCallback(
    async (payload?: IDraftGroupCharacter) => {
      const groupPayload = payload ?? prepareGroupPayload();

      setStateIsPromptEnabled(false);
      const { error } = (await onSave?.(groupPayload, activeLorebooks, stateInitialLorebooks, { hasGroupContentChanged })) || {};
      if (error) {
        hasChangedLorebooksRef.current = false;
        setStateCurrentLorebooks(contextFormData.lorebooks ?? lorebooks);
        setStateInitialLorebooks(lorebooks);
        return { error };
      }

      setSuccessMessage(t("createGroup:toast.group.savedSuccess"));

      setContextReferenceValues(values);
      hasChangedLorebooksRef.current = false;
      setStateInitialLorebooks(activeLorebooks);

      // Clear context data after successful save
      updateContextFormData({});
      setContextMembers([]);
      setContextDraftId(undefined);

      // removing draft if it exists
      if (draftId) {
        deleteDraft(draftId);
      } else if (isAlreadyDraft && id) {
        deleteDraft(id);
        navigate(ROUTES.groupchatCreate);
      }
      setTimeout(() => {
        setSuccessMessage("");
      }, SUCCESS_MESSAGE_TIMEOUT_MS);
    },
    [
      deleteDraft,
      draftId,
      id,
      isAlreadyDraft,
      navigate,
      onSave,
      prepareGroupPayload,
      setContextDraftId,
      setContextMembers,
      setContextReferenceValues,
      lorebooks,
      contextFormData.lorebooks,
      activeLorebooks,
      stateInitialLorebooks,
      t,
      updateContextFormData,
      values,
      hasGroupContentChanged,
    ],
  );

  const submitForm = useCallback(async () => {
    const payload = prepareGroupPayload();

    // `!isTokenCounterReady` guard: while the `gpt-token-utils` chunk is
    // still loading, counts are stubbed at 0 so the validator cannot
    // detect over-limit pre-populated content on edit forms. Block
    // submission until counts are trustworthy.
    if (!groupChatForm.isValid || stateIsTokenError || !isTokenCounterReady) {
      setStateIsRequiredFieldsErrorShown(true);
      const fieldOrder = [
        GroupChatFormFields.COVER,
        GroupChatFormFields.NAME,
        GroupChatFormFields.TITLE,
        GroupChatFormFields.SCENARIO,
        GroupChatFormFields.GREETING,
        GroupChatFormFields.TAGS,
        GroupChatFormFields.VISIBILITY,
        GroupChatFormFields.MEMBERS,
        GroupChatFormFields.TYPE,
        GroupChatFormFields.SUB_CHARACTERS_ID,
        GroupChatFormFields.FIRST,
      ];

      scrollToFirstInvalidField(findFirstInvalidFormizField({ fields: groupChatForm.fields, fieldOrder }));
      return;
    }

    const isCreatingPublicGroupChat = payload.visibility === GROUP_CHAT_VISIBILITY.PUBLIC && visibility !== payload.visibility;
    const wasGroupChatModerated = moderationStatus === GROUPCHAT_MODERATION_STATUS.FIX_REQUIRED;
    const isPartnerApplication = THIRD_PARTY_APP_IDS.includes(applicationId as string);

    if (!isPartnerApplication && (isCreatingPublicGroupChat || wasGroupChatModerated || submitForManualReview))
      return setStateIsConfirmationModalVisible(true);

    await handleSave(payload);
  }, [
    applicationId,
    groupChatForm,
    handleSave,
    isTokenCounterReady,
    moderationStatus,
    prepareGroupPayload,
    submitForManualReview,
    stateIsTokenError,
    visibility,
  ]);

  const handleFormizSubmit = useCallback(() => {
    void submitForm();
  }, [submitForm]);

  const handleGreetingCharacterChange = useCallback(
    ({ greetingCharacterId }: { greetingCharacterId?: string | null }) => {
      setFieldsValues({ [GroupChatFormFields.FIRST]: greetingCharacterId ?? (isEdit ? null : undefined) });
    },
    [isEdit, setFieldsValues],
  );

  useEffect(() => {
    if (errorCode) {
      displayToast({ message: CUSTOM_ERROR_MESSAGES[errorCode as CustomErrorMessage], type: NotificationType.error });
    }
  }, [displayToast, errorCode]);

  const onModalClose = () => {
    setStateIsConfirmationModalVisible(false);
  };

  const handleShowDetachLorebookModal = useCallback(() => {
    setStateIsDetachLorebookModalOpen(true);
  }, []);

  const handleCloseDetachLorebookModal = useCallback(() => {
    setStateIsDetachLorebookModalOpen(false);
  }, []);

  const handleConfirmDetachLorebook = useCallback(() => {
    hasChangedLorebooksRef.current = true;
    setStateCurrentLorebooks([]);
    setStateIsDetachLorebookModalOpen(false);
  }, []);

  const handleShowLorebookModal = useCallback(() => {
    setStateIsLorebookModalOpen(true);
  }, []);

  const handleCloseLorebookModal = useCallback(() => {
    setStateIsLorebookModalOpen(false);
  }, []);

  const handleLorebookAttached = useCallback(
    (lorebook: Lorebook) => {
      hasChangedLorebooksRef.current = true;
      setStateCurrentLorebooks([lorebook]);
      displayToast({ message: `${t("chatbotCard:tooltip.lorebookAttachedToast")} ${lorebook.name}`, type: NotificationType.success });
    },
    [displayToast],
  );

  const prepareDraftPayload = useCallback((): IDraftGroupCharacter => {
    const payload = Object.fromEntries(Object.entries(values).filter(([, value]: [string, unknown]) => value !== undefined)) as IDraftGroupCharacter;
    const dId = id || draftId || uuidv4();

    payload.id = dId;
    payload.creator_username = username ?? undefined;
    payload.creator_user_id = userId ?? undefined;
    payload[GroupChatFormFields.FIRST] = normalizeGreetingCharacterId(values[GroupChatFormFields.FIRST] as string | null | undefined);
    payload.lorebooks = activeLorebooks;
    payload.has_lorebooks = activeLorebooks.length > 0;

    if (membersValue && membersValue.length > 0) {
      payload.members = membersValue;
    }

    if (moderationStatus) {
      payload.moderationReport = { moderation_status: moderationStatus };
    }

    return payload;
  }, [activeLorebooks, values, id, draftId, username, userId, membersValue, moderationStatus]);

  const saveAsDraft = useCallback(() => {
    try {
      setIsSavingDraft(true);
      const payload = prepareDraftPayload();
      const isSuccess = saveDraft(payload);

      if (!isSuccess) {
        setIsSavingDraft(false);
        throw new Error("Failed to save draft");
      }

      // Update referenceValues
      setStateIsPromptEnabled(false);
      setContextReferenceValues(values);
      hasChangedLorebooksRef.current = false;
      setStateInitialLorebooks(activeLorebooks);

      displayToast({ type: NotificationType.success, message: t("createGroup:toast.group.savedSuccess") });

      if (!draftId && payload.id) {
        setTimeout(() => {
          navigate(`/group/create/${payload.id}`, { replace: true });
          setIsSavingDraft(false);
        }, 0);
      } else {
        setIsSavingDraft(false);
      }
    } catch {
      setIsSavingDraft(false);
      displayToast({ type: NotificationType.error, message: t("createGroup:toast.error.generic") });
    }
  }, [prepareDraftPayload, saveDraft, displayToast, navigate, draftId, values, setContextReferenceValues, activeLorebooks]);

  const getValidationSummary = () => {
    const errors: string[] = [];
    Object.keys(groupChatForm.fields).forEach((fieldName: string) => {
      const field = groupChatForm.fields[fieldName];
      if (field.error) {
        errors.push(field.error);
      }
    });

    return errors;
  };

  const isSomeRequiredFieldFilled = groupChatRequiredFields.some((field: string) => groupChatForm.fields[field]?.isValid);

  const isActionButtonVisuallyDisabled =
    isWaitingForApproval ||
    !isSomeRequiredFieldFilled ||
    stateIsTokenError ||
    !isTokenCounterReady ||
    isSubmitting ||
    (isEdit && !hasFormChanged && !draftId && !isAlreadyDraft);

  const handleSaveButtonClick = useCallback(() => {
    if (isWaitingForApproval) {
      displayToast({ message: t("error:editUnderReviewItem"), type: NotificationType.info });
      return;
    }
    if (isSubmitting) {
      return;
    }
    if (isEdit && !hasFormChanged && !draftId && !isAlreadyDraft) {
      displayToast({ message: t("error:noChangesDetected"), type: NotificationType.info });
      return;
    }
    groupChatForm.submit();
  }, [displayToast, draftId, groupChatForm, hasFormChanged, isAlreadyDraft, isEdit, isSubmitting, t, isWaitingForApproval]);

  const fieldTokensLength = useCallback(
    (name: string) =>
      countTokens(
        replaceMessageTokens(values[name], {
          characterName: values.name,
        }),
      ),
    [values, countTokens],
  );
  const fieldTokensString = (name: string) => {
    const length = fieldTokensLength(name);
    const charCount = values[name]?.length || 0;
    const characterCountLabel = t("createGroup:form.label.charactersCount", { count: charCount, length: length });
    return characterCountLabel;
  };
  const totalTokens = useMemo(() => {
    return fieldTokensLength(GroupChatFormFields.GREETING) + fieldTokensLength(GroupChatFormFields.SCENARIO);
  }, [fieldTokensLength]);

  useEffect(() => {
    const { isValid, errorMessage } = isCharacterTokenCountValid(totalTokens, groupChatVisibility as GROUP_CHAT_VISIBILITY, "v2");
    setStateIsTokenError(!isValid);
    setTokenErrorMessage(errorMessage || "");
    setTokenWarningMessage(totalTokens > TOKEN_LIMIT && isValid ? "Token count exceeds recommended limit" : "");
  }, [totalTokens, groupChatVisibility]);

  const handleBotVisibilityChange = (visibility: CHARACTER_VISIBILITY) => {
    setGroupChatVisibility(visibility);
    setFieldsValues({ [GroupChatFormFields.VISIBILITY]: visibility });
  };

  useEffect(() => {
    if (submitForManualReview) {
      setStateIsConfirmationModalVisible(true);
    }
  }, [submitForManualReview]);

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    const currentValues = groupChatForm.values;

    const hasChanged = prevValues !== JSON.stringify(currentValues);

    if (hasChanged && submitForManualReview) {
      setIsSubmitForManualReview(false);
    }

    prevValuesRef.current = JSON.stringify(currentValues);
  }, [groupChatForm.values]);

  // Validate greeting_character_id
  useEffect(() => {
    const currentGreetingCharacterId = values[GroupChatFormFields.FIRST];

    if (currentGreetingCharacterId) {
      const isGreeterStillMember = membersValue?.some((member: GroupMembersChatBot) => member.id === currentGreetingCharacterId);

      if (!isGreeterStillMember) {
        setFieldsValues({ [GroupChatFormFields.FIRST]: null });
      }
    }
  }, [membersValue, values[GroupChatFormFields.FIRST], setFieldsValues]);

  function handleAddRemoveMembers() {
    // Save current form data to context before navigating to members page
    const currentFormData: Partial<IDraftGroupCharacter> = {
      avatar_url: values[GroupChatFormFields.COVER],
      greeting_character_id: normalizeGreetingCharacterId(values[GroupChatFormFields.FIRST] as string | null | undefined),
      greeting: values[GroupChatFormFields.GREETING],
      guidelines: values[GroupChatFormFields.GUIDELINES],
      name: values[GroupChatFormFields.NAME],
      members: membersValue,
      scenario: values[GroupChatFormFields.SCENARIO],
      tags: values[GroupChatFormFields.TAGS],
      title: values[GroupChatFormFields.TITLE],
      visibility: values[GroupChatFormFields.VISIBILITY],
      lorebooks: activeLorebooks,
      has_lorebooks: activeLorebooks.length > 0,
    };

    updateContextFormData(currentFormData);
    setContextDraftId(draftId || id);

    navigate(ROUTES.groupChatCreateMembers, {
      state: {
        groupVisibility: groupChatVisibility,
        fromPath: pathname,
      },
    });
  }

  const visibilityConflictMessage = useMemo(() => {
    if (conflictingMembers.length === 0) return "";
    const message =
      groupChatVisibility === GROUP_CHAT_VISIBILITY.PUBLIC
        ? t("createGroup:validation.members.visibilityConflict.public")
        : t("createGroup:validation.members.visibilityConflict.unlisted");
    return `${message}:\n
    ${conflictingMembers.map((member: GroupMembersChatBot) => member.name).join(", ")}`;
  }, [conflictingMembers, groupChatVisibility]);

  return (
    <>
      <ReactRouterPrompt
        when={({ currentLocation, nextLocation }: Parameters<BlockerFunction>[0]) => {
          // No changes to save
          if (!isPromptVisible) return false;

          // Allow language-only changes
          const currentPath = stripLanguagePrefix(currentLocation.pathname);
          const nextPath = stripLanguagePrefix(nextLocation.pathname);
          if (currentPath === nextPath) return false;

          // Allow navigation to members page
          if (nextLocation.pathname.includes("/members")) return false;

          // Block navigation outside groupchat routes
          if (!nextLocation.pathname.startsWith(ROUTES.groupchatCreate)) return true;

          // Check if we're navigating from draft to new
          const hasCurrentDraftId = currentLocation.pathname !== ROUTES.groupchatCreate;
          const isNextBase = nextLocation.pathname === ROUTES.groupchatCreate;

          // Allow: /group/create to /group/create/:draftId
          if (!hasCurrentDraftId && !isNextBase) return false;

          // Check: /group/create/:draftId to /group/create (draft to new)
          if (hasCurrentDraftId && isNextBase) return isPromptVisible;

          // Allow all other navigation within groupchat routes
          return false;
        }}
      >
        {({ isActive, onConfirm, onCancel }: { isActive: boolean; onConfirm: () => void; onCancel: () => void }) =>
          isActive ? (
            <DiscardChangesModalV2
              heading={t("common:modal.unsavedChanges.title")}
              description={t("common:modal.unsavedChanges.message")}
              isVisible={true}
              onUserConfirmation={() => {
                resetForm();
                onConfirm();
              }}
              handleClose={onCancel}
            />
          ) : null
        }
      </ReactRouterPrompt>

      <Formiz connect={groupChatForm} initialValues={initialValues} onSubmit={handleFormizSubmit}>
        <form onSubmit={groupChatForm.submit}>
          <fieldset className="flex flex-col gap-6 pt-6 border-none">
            <FormField
              disabled={isWaitingForApproval}
              required
              showError={stateIsRequiredFieldsErrorShown}
              name={GroupChatFormFields.COVER}
              label={t("createGroup:form.field.coverImage.label")}
              as="custom"
              description={t("createGroup:form.field.coverImage.description")}
              data-field-name={GroupChatFormFields.COVER}
              validations={[
                {
                  rule: isRequired(),
                  message: t("createGroup:validation.avatar.required"),
                },
              ]}
            >
              <AvatarCreate
                disabled={isWaitingForApproval}
                characterAvatar={values[GroupChatFormFields.COVER]}
                setCharacterAvatar={(value: string) => {
                  groupChatForm.setFieldsValues({ [GroupChatFormFields.COVER]: value });
                }}
                form={groupChatForm}
                formType={FormTypes.CHARACTER}
              />
            </FormField>

            <FormField
              disabled={isWaitingForApproval}
              showError={stateIsRequiredFieldsErrorShown}
              name={GroupChatFormFields.NAME}
              as="custom"
              data-field-name={GroupChatFormFields.NAME}
              validations={[
                {
                  rule: (value: string) => isRequired()(value?.trim()),
                  message: t("createGroup:validation.field.required"),
                },
                {
                  rule: (value: string) => isMinLength(MIN_FIELD_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.field.minLength", { min: MIN_FIELD_LENGTH }),
                },
                {
                  rule: (value: string) => isMaxLength(MAX_NAME_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.name.maxLength", { max: MAX_NAME_LENGTH }),
                },
                {
                  rule: (value: string) => !isStringContainingURL(value),
                  message: nameUrlErrorMessage,
                },
              ]}
            >
              <Input
                label={t("createGroup:form.field.name.label")}
                helperText={t("createGroup:form.field.name.helper")}
                placeholder={t("createGroup:form.field.name.placeholder")}
                maxLength={MAX_NAME_LENGTH}
                aria-labelledby={t("createGroup:form.field.name.label")}
                disabled={isWaitingForApproval}
                required
              />
            </FormField>

            <FormField
              disabled={isWaitingForApproval}
              showError={stateIsRequiredFieldsErrorShown}
              name={GroupChatFormFields.TITLE}
              as="textarea"
              data-field-name={GroupChatFormFields.TITLE}
              validations={[
                {
                  rule: (value: string) => isRequired()(value?.trim()),
                  message: t("createGroup:validation.field.required"),
                },
                {
                  rule: (value: string) => isMinLength(MIN_FIELD_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.field.minLength", { min: MIN_FIELD_LENGTH }),
                },
                {
                  rule: (value: string) => isMaxLength(MAX_TITLE_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.title.maxLength", { max: MAX_TITLE_LENGTH }),
                },
                {
                  rule: (value: string) => !isStringContainingURL(value),
                  message: titleUrlErrorMessage,
                },
              ]}
            >
              <TextArea
                label={t("createGroup:form.field.title.label")}
                helperText={t("createGroup:form.field.title.helper")}
                placeholder={t("createGroup:form.field.title.placeholder")}
                maxLength={MAX_TITLE_LENGTH}
                rows={2}
                maxRows={20}
                aria-labelledby={t("createGroup:form.field.title.label")}
                disabled={isWaitingForApproval}
                required
              />
            </FormField>

            <div>
              <FormField
                disabled={isWaitingForApproval}
                showError={stateIsRequiredFieldsErrorShown}
                required={true}
                name={GroupChatFormFields.MEMBERS}
                as="custom"
                data-field-name={GroupChatFormFields.MEMBERS}
                label={`${t("createGroup:form.field.members.label")} (${membersValue?.length || 0} / ${maxParticipants})`}
                validations={[
                  {
                    rule: () => isMemberCountValid,
                    message: t("createGroup:validation.members.minCount", { min: MINIMUM_PARTICIPANT_LIMIT }),
                    deps: [isMemberCountValid],
                  },
                  {
                    rule: () => !hasMembersWithErrors,
                    message: visibilityConflictMessage || t("createGroup:validation.members.visibilityConflict"),
                    deps: [hasMembersWithErrors, visibilityConflictMessage],
                  },
                ]}
              >
                <MembersGroupChat
                  members={membersValue}
                  maxMembers={maxParticipants}
                  mode={MembersGroupChatDisplayMode.NORMAL}
                  onAddRemove={handleAddRemoveMembers}
                  hideHeader={true}
                  disabled={isWaitingForApproval}
                  visibility={groupChatVisibility}
                  isEdit={isEdit}
                />
              </FormField>
            </div>

            <div className={cn("border-solid border-x-0 border-t-0 border-b border-gray-9 pb-5", !isMemberCountValid && "opacity-50")}>
              <FormField
                disabled={isWaitingForApproval}
                showError={stateIsRequiredFieldsErrorShown}
                as="custom"
                name={GroupChatFormFields.FIRST}
                data-field-name={GroupChatFormFields.FIRST}
                label={t("createGroup:form.field.firstMessage.label")}
                description={t("createGroup:form.field.firstMessage.description")}
              >
                <FormFieldValueSink />
              </FormField>
              {isMemberCountValid ? (
                <RadioGroup
                  className="!grid !grid-cols-[repeat(auto-fill,_minmax(140px,_1fr))] !gap-x-8 pt-5 !justify-start"
                  value={values[GroupChatFormFields.FIRST]}
                  onValueChange={(greetingCharacterId: string | null) => handleGreetingCharacterChange({ greetingCharacterId })}
                  items={membersValue?.map((member: GroupMembersChatBot) => ({
                    label: member.name,
                    value: member.id,
                    disabled: !isFirstMessageSelectionEnabled,
                  }))}
                  allowDeselect
                />
              ) : null}
            </div>

            <FormField
              disabled={isWaitingForApproval}
              showError={stateIsRequiredFieldsErrorShown || stateIsTokenError}
              name={GroupChatFormFields.GREETING}
              as="custom"
              data-field-name={GroupChatFormFields.GREETING}
              validations={[
                {
                  rule: (value: string) => isRequired()(value?.trim()),
                  message: t("createGroup:validation.field.required"),
                },
                {
                  rule: (value: string) => isMinLength(MIN_FIELD_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.field.minLength", { min: MIN_FIELD_LENGTH }),
                },
                {
                  rule: (value: string) => isMaxLength(MAX_GREETING_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.greeting.maxLength", { max: MAX_GREETING_LENGTH }),
                },
                {
                  rule: (value: string) => !isStringContainingURL(value),
                  message: greetingUrlErrorMessage,
                },
              ]}
            >
              <TextArea
                label={t("createGroup:form.field.greeting.label")}
                labelRightElement={
                  <GenerateSuggestedFieldButton
                    field={GroupChatFormFields.GREETING}
                    onButtonClick={() => handleGenerateSuggestField(GroupChatFormFields.GREETING)}
                    disabled={
                      isWaitingForApproval ||
                      !isTitleAndNameFilled ||
                      (activeGeneratingField !== null && activeGeneratingField !== GroupChatFormFields.GREETING)
                    }
                    isLoading={activeGeneratingField === GroupChatFormFields.GREETING}
                  />
                }
                helperText={t("createGroup:form.field.greeting.helper")}
                maxLength={MAX_GREETING_LENGTH}
                showMaxLengthLabel={false}
                rows={2}
                maxRows={20}
                aria-labelledby={t("createGroup:form.field.greeting.label")}
                countLabel={fieldTokensString(GroupChatFormFields.GREETING)}
                placeholder={t("createGroup:form.field.greeting.placeholder")}
                required
                disabled={isWaitingForApproval}
              />
            </FormField>

            <FormField
              required={isGroupVisibilityPublic}
              showError={stateIsRequiredFieldsErrorShown}
              label={t("createGroup:form.field.tags.label")}
              name={GroupChatFormFields.TAGS}
              as="custom"
              data-field-name={GroupChatFormFields.TAGS}
              validations={[
                {
                  rule: () => isSelectedTagsValid,
                  message: t("createGroup:validation.tags.minCount", { min: MINIMUM_TAG_LIMIT }),
                  deps: [isSelectedTagsValid],
                },
              ]}
              description={t("createGroup:form.field.tags.description")}
              countLabel={`${values[GroupChatFormFields.TAGS]?.length || 0}/${MAXIMUM_TAG_LIMIT}`}
            >
              <CharacterTags
                disabled={isWaitingForApproval}
                initialValues={groupChatForm?.values?.[GroupChatFormFields.TAGS]}
                characterFields={{
                  name: groupChatForm?.values?.[GroupChatFormFields.NAME],
                  title: groupChatForm?.values?.[GroupChatFormFields.TITLE],
                  greeting: groupChatForm?.values?.[GroupChatFormFields.GREETING],
                  scenario: groupChatForm?.values?.[GroupChatFormFields.SCENARIO],
                }}
                setSelectedTags={(value: { label: string; value: string }[]) => {
                  groupChatForm.setFieldsValues({ [GroupChatFormFields.TAGS]: value });
                }}
              />
            </FormField>

            <div className={cn("contents", visibilityConflictMessage && "[&_p+div+p]:text-red-9 [&_p+div+p]:max-w-[38ch]")}>
              <VisibilityButtons
                name={GroupChatFormFields.VISIBILITY}
                validations={[
                  {
                    rule: (value: string) => isRequired()(value?.trim()),
                    message: t("createGroup:validation.visibility.required"),
                  },
                ]}
                buttons={[
                  {
                    value: "public",
                    label: t("createGroup:form.field.visibility.public.label"),
                    helperText: visibilityConflictMessage || t("createGroup:form.field.visibility.public.helper"),
                  },
                  {
                    value: "private",
                    label: t("createGroup:form.field.visibility.private.label"),
                    helperText: t("createGroup:form.field.visibility.private.helper"),
                  },
                  {
                    value: "hidden",
                    label: t("createGroup:form.field.visibility.unlisted.label"),
                    helperText: visibilityConflictMessage || t("createGroup:form.field.visibility.unlisted.helper"),
                  },
                ]}
                title={t("createGroup:form.field.visibility.label")}
                aria-labelledby={t("createGroup:form.field.visibility.label")}
                setBotVisibility={handleBotVisibilityChange}
                botVisibility={groupChatVisibility}
                isDisabled={isFixRequired || isWaitingForApproval || isPermanentlyPrivate}
              />
            </div>
            <FormField
              showError={stateIsRequiredFieldsErrorShown || stateIsTokenError}
              name={GroupChatFormFields.SCENARIO}
              as="textarea"
              data-field-name={GroupChatFormFields.SCENARIO}
              data-testid="CreateGroupForm-ScenarioSection"
              validations={[
                {
                  rule: (value: string) => isMinLength(MIN_FIELD_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.field.minLength", { min: MIN_FIELD_LENGTH }),
                },
                {
                  rule: (value: string) => isMaxLength(MAX_SCENARIO_LENGTH)(value?.trim()),
                  message: t("createGroup:validation.scenario.maxLength", { max: MAX_SCENARIO_LENGTH }),
                },
              ]}
            >
              <TextArea
                label={t("createGroup:form.field.scenario.label")}
                labelRightElement={
                  <GenerateSuggestedFieldButton
                    field={GroupChatFormFields.SCENARIO}
                    onButtonClick={() => handleGenerateSuggestField(GroupChatFormFields.SCENARIO)}
                    disabled={
                      isWaitingForApproval ||
                      !isTitleAndNameFilled ||
                      (activeGeneratingField !== null && activeGeneratingField !== GroupChatFormFields.SCENARIO)
                    }
                    isLoading={activeGeneratingField === GroupChatFormFields.SCENARIO}
                  />
                }
                helperText={t("createGroup:form.field.scenario.helper")}
                maxLength={MAX_SCENARIO_LENGTH}
                showMaxLengthLabel={false}
                rows={2}
                maxRows={20}
                aria-labelledby={t("createGroup:form.field.scenario.label")}
                countLabel={`${fieldTokensString(GroupChatFormFields.SCENARIO)}`}
                placeholder={t("createGroup:form.field.scenario.placeholder")}
                disabled={isWaitingForApproval}
              />
            </FormField>

            {hasGroupLorebookFeatureFlag ? (
              <LorebookSection
                attachedLorebook={stateCurrentLorebooks?.[0]}
                cardVariant="static"
                onAttach={handleShowLorebookModal}
                onReplace={handleShowLorebookModal}
                onDetach={handleShowDetachLorebookModal}
                disabled={isWaitingForApproval}
                isAutoDetached={isLorebookAutoDetached}
              />
            ) : null}

            <TokenCounter
              currentTokens={totalTokens}
              maxTokens={MAX_TOKEN_LIMIT}
              hasError={stateIsTokenError}
              hasWarning={!stateIsTokenError && !!tokenWarningMessage}
              errorMessage={tokenErrorMessage}
              warningMessage={tokenWarningMessage}
              helperText={t("createGroup:form.field.tokens.helper")}
              label={t("createGroup:form.field.tokens.label")}
              infoLinkSlot={
                <LocalizedLink
                  target="_blank"
                  to={`${EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.docs}/advanced/tokens`}
                  aria-label="token-limit-information"
                >
                  <Icon icon={IconVariants.INFO} size={IconSize.SMALL} className="cursor-pointer text-gray-12" />
                </LocalizedLink>
              }
            />

            <div className={cn(isEdit && "hidden")}>
              <FormField
                name={GroupChatFormFields.GUIDELINES}
                as="checkbox"
                showError={stateIsRequiredFieldsErrorShown}
                data-field-name={GroupChatFormFields.GUIDELINES}
                validations={[
                  {
                    rule: (value: boolean) => isEdit || value === true,
                    message: t("createGroup:validation.guidelines.agreeRequired"),
                  },
                ]}
              >
                <Checkbox
                  disabled={isWaitingForApproval}
                  name="guidelines"
                  aria-labelledby="guidelines"
                  size={Size.MEDIUM}
                  label={
                    <Text variant={TextVariants.LABEL_MEDIUM_REGULAR}>
                      <Trans
                        i18nKey="createGroup:form.field.guidelines.label"
                        components={{
                          communityGuidelinesLink: (
                            <LocalizedLink
                              className="text-blue-10"
                              target="_blank"
                              to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.guidelines}
                              aria-label="community-guidelines"
                            />
                          ),
                        }}
                      />
                    </Text>
                  }
                />
              </FormField>
            </div>
            {((!isValid && !isStepPristine) || !isSelectedTagsValid) && (
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
                {error}
              </Text>
            )}
            {submitError ? (
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
                {submitError}
              </Text>
            ) : null}

            {successMessage && !submitForManualReview ? (
              <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-green-9">
                {successMessage}
              </Text>
            ) : null}

            {/* Validation error summary */}
            {!isStepPristine && !isValid && Boolean(getValidationSummary().length) && (
              <div className="flex flex-col md:flex-row gap-2">
                {getValidationSummary().map((error: string, index: number) => (
                  <div key={index} className="w-full md:w-1/2">
                    <Text variant={TextVariants.LABEL_MEDIUM_REGULAR} className="text-red-9">
                      {error}
                    </Text>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col md:flex-row gap-2 justify-end">
              {!isEdit || isFixRequired ? (
                <Button
                  onClick={saveAsDraft}
                  variant={ButtonVariant.OUTLINED}
                  size={ButtonSize.LARGE}
                  aria-labelledby={isAlreadyDraft || draftId ? t("createGroup:form.button.updateDraft") : t("createGroup:form.button.saveAsDraft")}
                  loading={isSavingDraft}
                  loadingWithText={true}
                  disabled={(!hasFormChanged && !isAlreadyDraft && isFixRequired) || isWaitingForApproval || isSavingDraft}
                >
                  {isAlreadyDraft || draftId ? t("createGroup:form.button.updateDraft") : t("createGroup:form.button.saveAsDraft")}
                </Button>
              ) : null}
              {onSave ? (
                <Button
                  size={ButtonSize.LARGE}
                  type="button"
                  onClick={handleSaveButtonClick}
                  aria-labelledby={isEdit ? t("createGroup:form.button.updateGroup") : t("createGroup:form.button.createAndChat")}
                  loading={isSubmitting}
                  loadingWithText={true}
                  aria-disabled={isActionButtonVisuallyDisabled}
                  className={isActionButtonVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
                >
                  {isEdit ? t("createGroup:form.button.updateGroup") : t("createGroup:form.button.createAndChat")}
                </Button>
              ) : null}
            </div>
          </fieldset>
        </form>
      </Formiz>

      <SubmitForReviewModal
        title={t("createGroup:modal.submitForReview.heading")}
        onUserConfirmation={handleSave}
        confirmationMessage={confirmText}
        isVisible={stateIsConfirmationModalVisible}
        actionButtonText={
          submitForManualReview ? t("createGroup:modal.submitForReview.actionButtonManual") : t("createGroup:modal.submitForReview.actionButton")
        }
        discardButtonText={t("common:cancel")}
        isLoading={isSubmitting}
        handleClose={onModalClose}
      />

      <Modal
        title={t("createGroup:modal.detachLorebook.title")}
        aria-labelledby="detach lorebook modal"
        open={stateIsDetachLorebookModalOpen}
        onOpenChange={handleCloseDetachLorebookModal}
        description={t("createGroup:modal.detachLorebook.message")}
        actions={[
          {
            label: t("common:cancel"),
            onClick: handleCloseDetachLorebookModal,
            variant: ButtonVariant.OUTLINED,
            size: ButtonSize.LARGE,
          },
          {
            label: t("createGroup:modal.detachLorebook.action"),
            onClick: handleConfirmDetachLorebook,
            size: ButtonSize.LARGE,
            color: ButtonColorVariant.ERROR,
          },
        ]}
      />
      {hasGroupLorebookFeatureFlag ? (
        <ModalLorebook
          isOpen={stateIsLorebookModalOpen}
          onClose={handleCloseLorebookModal}
          selectedLorebook={stateCurrentLorebooks?.[0] ?? null}
          onLorebookAttached={handleLorebookAttached}
          dataTestId="CreateGroupFormModalLorebook"
        />
      ) : null}
    </>
  );
}

export default CreateGroupForm;
